import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxButtonModule, DxFormModule, DxScrollViewModule, DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular';
import { DxLoadPanelModule } from 'devextreme-angular/ui/load-panel';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { alert } from 'devextreme/ui/dialog';
import {
  FormCheckboxComponent,
  FormDateboxComponent,
  FormNumberboxComponent,
  FormSelectboxComponent,
  FormTextboxComponent
} from 'src/app/components';
import { FormNoteComponent } from 'src/app/components/form-note.component';
import { SubstanceAbuseTreatmentReferral } from 'src/app/models/substance-abuse-treatment-referral';
import { AppInfoService, AuthService } from 'src/app/services';
import { FormService } from 'src/app/services/form.service';
import { CardComponent } from '../../components/library/card/card.component';
import { FormAttachmentComponent } from 'src/app/components/utils/form-attachment.component';
import { FormExportComponent } from 'src/app/components/utils/form-export.component';

@Component({
  templateUrl: './sat-referral.component.html',
  styleUrls: ['./sat-referral.component.scss'],
  imports: [
    CommonModule,
    DxToolbarModule,
    DxButtonModule,
    DxLoadPanelModule,
    DxScrollViewModule,
    DxValidationGroupModule,
    DxFormModule,
    FormTextboxComponent,
    FormDateboxComponent,
    FormSelectboxComponent,
    FormCheckboxComponent,
    FormNumberboxComponent,
    FormAttachmentComponent,
    CardComponent,
    FormNoteComponent,
    FormAttachmentComponent,
    FormExportComponent
  ]
})
export class SatReferralComponent implements OnInit {
  formService = inject(FormService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  appInfo = inject(AppInfoService);

  model = signal<SubstanceAbuseTreatmentReferral>(null);
  isEditing = signal<boolean>(false);
  isLoading = this.appInfo.isLoading;
  key = signal<number | null>(null);

  Forms = FormService.Forms;

  drugOptions = ['Alcohol', 'Cocaine', 'Opiates', 'Meth', 'Other'];
  administrationMethodOptions = ['Injection', 'Oral', 'Nasal', 'Smoke'];
  frequencyOptions = ['Hourly', 'Daily', 'Weekly', 'Monthly', 'Yearly'];
  offenseHistoryOptions = ['Arson', 'Sex Offense', 'OUIL 3rd', 'Other'];
  medicalConditionOptions = ['Cardiac', 'Back', 'Diabetes', 'High BP', 'Pregnancy', 'Seizure', 'Other'];

  isContentScrolled = false;

  validationGroup = viewChild<DxValidationGroupComponent>('validationGroup');
  formAttachment = viewChild<FormAttachmentComponent>('formAttachment');

  async ngOnInit() {
    this.route.params.subscribe(async (params) => {
      this.isLoading.set(true);
      this.isEditing.set(false);

      const key = params['id'];
      if (key) {
        this.key.set(key);
        const data = await this.formService.get<SubstanceAbuseTreatmentReferral>(FormService.Forms.SatReferral, key);
        this.model.set(data);
      } else {
        this.model.set(new SubstanceAbuseTreatmentReferral());
        this.isEditing.set(true);
      }

      this.isLoading.set(false);
    });
  }

  scroll({ reachedTop = false }) {
    this.isContentScrolled = !reachedTop;
  }

  async submit() {
    const isValid = this.validationGroup().instance.validate().isValid;
    if (!isValid) {
      await alert('Please make sure all parts of the form are filled in correctly.', 'Unable to submit form');
      return;
    }

    this.isLoading.set(true);
    this.cleanModel();
    const result = await this.formService.post<SubstanceAbuseTreatmentReferral>(FormService.Forms.SatReferral, this.model());
    this.isLoading.set(false);
    if (!result.isSuccess) {
      await alert('The form was unable to be submitted', 'Error');
      return;
    }

    const id = result.value?.id;
    //TODO: this.key() is being set, but it isn't changing the formAttachmentComponent.FormId
    this.key.set(id);
    await this.formAttachment().submit(id);

    await this.router.navigate(['/thank-you']);
  }

  cleanModel() {
    if (!this.model().secondaryDrug) {
      this.model().secondaryDrugAdministrationMethod = null;
      this.model().secondaryDrugFrequency = null;
      this.model().secondaryDrugLastUse = null;
    }
    if (!this.model().hasRecentFailedTreatment) this.model().recentFailedTreatmentDate = null;
    if (!this.model().outpatientTreatmentReceived) {
      this.model().outpatientTreatmentAmount = null;
      this.model().outpatientTreatmentDates = null;
    }
    if (!this.model().residentialTreatmentReceived) {
      this.model().residentialTreatmentAmount = null;
      this.model().residentialTreatmentDates = null;
    }
    if (!this.model().psychiatricProblemsExist) this.model().psychiatricProblemDetails = null;
    if (!this.model().onMedication) this.model().medicationDetails = null;
    if (this.model().medicationIsImmediatelyAvailable) this.model().medicationAvailableDate = null;
    if (this.model().primaryDrug !== 'Other') this.model().primaryDrugOther = null;
    if (this.model().secondaryDrug !== 'Other') this.model().secondaryDrugOther = null;
    if (this.model().currentMedicalCondition !== 'Other') this.model().currentMedicalConditionOther = null;
  }
}
